import { TripPingLight } from './trip-ping-light.model';
import { DistanceUtils } from '../../shared/distance-utils';
import { Trip } from './trip.model';
import { TripDevicePath, TripPath, TripPathOptions } from './trip-path.model';

export class TripPathUtils {

  private static RADIUS_DIST_FROM_DEST_THRESHOLD_IN_M = 1000;
  private static RADIUS_DIST_OUTSIDE_OF_DEST_THRESHOLD_IN_M = 1500;

  /**
   * Build a list of chained paths for each different devices from their associated pings.
   * The paths are divided following consistency rules described by: {@link TripPathUtils.finalizeCurrentPathIfNextPosIsIncoherent}.
   * This will define {@link Trip.achievedPaths} property from the {@link Trip} object.
   *
   * @param trip    for which the paths are built
   * @param pings   associated pings to the trip
   * @param options related options
   */
  public static buildPathsFromPingsByDevice(trip: Trip, pings: TripPingLight[], options: TripPathOptions): TripDevicePath[] {
    const result: TripDevicePath[] = [];
    const pingsGroupedByDeviceId   = this.groupPingsByDeviceId(pings);

    for (const deviceId in pingsGroupedByDeviceId) {
      if (!pingsGroupedByDeviceId.hasOwnProperty(deviceId)) {
        continue;
      }

      let devicePings = pingsGroupedByDeviceId[deviceId];

      if (options.hidePingsAfterDestinationHit) {
        devicePings = this.filterPingsAfterDestinationHit(trip, devicePings);
      }

      let paths: TripPath[]                 = [];
      let pingsAccumulator: TripPingLight[] = [];

      for (let i = 0; i < devicePings.length - 1; i++) {
        const currentPos     = devicePings[i];
        const nextPos = devicePings[i + 1];

        pingsAccumulator.push(currentPos);

        if (this.finalizeCurrentPathIfNextPosIsIncoherent(currentPos, nextPos, paths, pingsAccumulator)) {
          pingsAccumulator = [];
        }

        if (i === devicePings.length - 2) {
          // If we are in the last loop, we add the next ping (the last one from 'devicePings') to the accumulator
          pingsAccumulator.push(nextPos);
        }
      }

      if (devicePings.length !== 0 && pingsAccumulator.length === 0) {
        paths.push(new TripPath([...devicePings], 0, 0));
      }

      if (pingsAccumulator.length > 0) {
        paths.push(new TripPath(pingsAccumulator, 0, 0));
      }

      if (options.excludeIsolatedPings) {
        paths = paths.filter(p => !p.canBeSkipped());
      }

      paths = paths.filter(p => p.pings.length > 0);

      if (paths.length > 0) {
        const devicePaths = new TripDevicePath(deviceId, paths);
        devicePaths.chainPathsBetweenOneAnother();
        result.push(devicePaths);
      }
    }

    return result;
  }

  /**
   * Compares the current position (or ping) with the next one and calculate the following:
   * - the distance between them
   * - the ellapsed time between them
   * - the theoretical speed to move from current to next pos
   *
   * If either: too much time ellapsed, too much distance between them or speed is too fast,
   * then, we finish the current path and make a new one
   *
   * @param currentPos
   * @param nextPos
   * @param paths
   * @param pingsAccumulator
   * @private
   */
  private static finalizeCurrentPathIfNextPosIsIncoherent(
    currentPos: TripPingLight,
    nextPos: TripPingLight,
    paths: TripPath[],
    pingsAccumulator: TripPingLight[]
  ): boolean {
    const distanceFromNextPosInKm = DistanceUtils.distanceBetweenCoords(currentPos.latitude, currentPos.longitude, nextPos.latitude, nextPos.longitude);
    const distanceFromNextPosInM  = distanceFromNextPosInKm * 1000;

    const ellapsedTimeFromNextPosInSeconds = nextPos.createdAt.diff(currentPos.createdAt, 'seconds');
    const ellapsedTimeFromNextPosInMinutes = ellapsedTimeFromNextPosInSeconds / 60;

    const speedKMPerHour = distanceFromNextPosInKm * 60 / (ellapsedTimeFromNextPosInSeconds / 60);
    const tooMuchTimeEllapsed = ellapsedTimeFromNextPosInMinutes > 60;
    const tooMuchDistance     = distanceFromNextPosInKm > 10;
    const tooFast             = speedKMPerHour > 120 && distanceFromNextPosInM > 2000;

    const nextPosIsIncoherent = tooMuchTimeEllapsed || tooMuchDistance || tooFast;
    if (nextPosIsIncoherent) {
      paths.push(new TripPath(pingsAccumulator, distanceFromNextPosInM, ellapsedTimeFromNextPosInSeconds));
    }
    return nextPosIsIncoherent;
  }

  /**
   * Get each trip ping sorted by creation date from the achieved paths of the trip
   *
   * @param trip
   */
  public static getAggregatedPingsFromTripSortedByCreation(trip: Trip): TripPingLight[] {
    return trip.achievedPaths
      .flatMap(paths => paths.paths.flatMap((path) => path.pings))
      .sort((a, b) => a.createdAt.diff(b.createdAt));
  }

  /**
   * Filter out the pings that are:
   * - happening after the first ping that is contained inside the distance radius from the destination
   * and
   * - are themselves outside the distance radius threshold
   *
   * i.e: Each pings that are too far from the destination after one of the ping is near enough from the destination
   *
   * @param trip
   * @param pings
   */
  public static filterPingsAfterDestinationHit(trip: Trip, pings: TripPingLight[]): TripPingLight[] {
    let enteredDestinationRadius = false;
    let closerPingDistance       = 10000;
    let closerPingIndex          = 0;

    for (let i = 0; i < pings.length; i++) {
      const ping         = pings[i];
      const distanceInKmLeftFromDestination = DistanceUtils.distanceBetweenCoords(ping.latitude, ping.longitude, trip.destPosition.lat, trip.destPosition.lng);
      const distanceInMLeftFromDestination  = distanceInKmLeftFromDestination * 1000;

      if (!enteredDestinationRadius) {
        if (distanceInMLeftFromDestination < this.RADIUS_DIST_FROM_DEST_THRESHOLD_IN_M) {
          console.log('enter !', pings.length, i);
          enteredDestinationRadius = true;
          closerPingDistance       = distanceInMLeftFromDestination;
          closerPingIndex          = i;
        }
      } else {
        if (distanceInMLeftFromDestination > this.RADIUS_DIST_OUTSIDE_OF_DEST_THRESHOLD_IN_M) {
          console.log('out !', pings.length, i, closerPingIndex);
          return pings.slice(0, closerPingIndex + 1);
        } else if (distanceInMLeftFromDestination < closerPingDistance) {
          closerPingDistance = distanceInMLeftFromDestination;
          closerPingIndex    = i;
        }
      }
    }

    return pings;
  }

  public static groupPingsByDeviceId(pings: TripPingLight[]): { [key: string]: TripPingLight[] } {
    return pings.reduce((map, ping) => {
      (map[ping.deviceId] = map[ping.deviceId] || []).push(ping);
      return map;
    }, {});
  }
}
